import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Trash2, Plus } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  FormInput,
  FormSelect,
  FormTextarea,
  PrimaryButton,
  SecondaryButton,
  Row,
} from "@/components/forms";
import { SkeletonLoader, useToast } from "@/components/system";
import { formatError } from "@/lib/format-error";
import { formatShortDate } from "@/lib/date-format";

const KINDS = [
  { value: "casual", label: "Casual" },
  { value: "logistical", label: "Logistical" },
  { value: "emotional", label: "Emotional" },
  { value: "professional", label: "Professional" },
  { value: "planning", label: "Planning" },
  { value: "mentorship", label: "Mentorship" },
  { value: "group_coordination", label: "Group coordination" },
];

const STATUSES = [
  { value: "active", label: "Active" },
  { value: "awaiting_me", label: "Awaiting me" },
  { value: "awaiting_them", label: "Awaiting them" },
  { value: "paused", label: "Paused" },
  { value: "closed", label: "Closed" },
];

const EVENT_KINDS = [
  { value: "message_sent", label: "Message sent" },
  { value: "message_received", label: "Message received" },
  { value: "call", label: "Call" },
  { value: "video_call", label: "Video call" },
  { value: "in_person_meeting", label: "In-person meeting" },
  { value: "meal", label: "Shared meal" },
  { value: "gift_sent", label: "Gift sent" },
  { value: "card_sent", label: "Card sent" },
  { value: "visit", label: "Visit" },
  { value: "shared_activity", label: "Shared activity" },
  { value: "no_contact_observation", label: "No-contact note" },
];

const EVENT_MEDIA = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "signal", label: "Signal" },
  { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "in_person", label: "In person" },
  { value: "video", label: "Video" },
  { value: "mail", label: "Mail" },
  { value: "gift", label: "Gift" },
  { value: "other", label: "Other" },
];

type ThreadRow = {
  id: string;
  user_id: string;
  title: string;
  kind: string;
  status: string;
  linked_person_ids: string[];
  linked_project_id: string | null;
  next_action: string | null;
  next_action_due: string | null;
  notes: string;
  last_event_ts: string | null;
  updated_at: string;
};

type EventRow = {
  id: string;
  thread_id: string;
  ts: string;
  kind: string;
  direction: string;
  medium: string;
  summary: string;
  notes: string;
};

type PersonLite = { id: string; display_name: string };

type ThreadForm = {
  title: string;
  kind: string;
  status: string;
  linked_person_ids: string[];
  linked_project_id: string | null;
  next_action: string;
  next_action_due: string;
  notes: string;
};

const EMPTY: ThreadForm = {
  title: "",
  kind: "casual",
  status: "active",
  linked_person_ids: [],
  linked_project_id: null,
  next_action: "",
  next_action_due: "",
  notes: "",
};

export function ThreadDetail() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { session } = useSession();
  const userId = session?.user?.id;

  const [form, setForm] = useState<ThreadForm>(EMPTY);
  const [hydrated, setHydrated] = useState(isNew);

  const peopleQ = useQuery<PersonLite[]>({
    queryKey: ["people_lite", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            eq: (col: string, v: string) => Promise<{ data: PersonLite[] | null; error: unknown }>;
          };
        };
      };
      const { data, error } = await client
        .from("person")
        .select("id, display_name")
        .eq("user_id", userId);
      if (error) throw error as Error;
      return (data ?? []).slice().sort((a, b) => a.display_name.localeCompare(b.display_name));
    },
  });

  const threadQ = useQuery<ThreadRow | null>({
    queryKey: ["thread", id],
    enabled: Boolean(userId) && !isNew,
    staleTime: 30_000,
    queryFn: async () => {
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            eq: (col: string, v: string) => {
              maybeSingle: () => Promise<{ data: ThreadRow | null; error: unknown }>;
            };
          };
        };
      };
      const { data, error } = await client.from("thread").select("*").eq("id", id ?? "").maybeSingle();
      if (error) throw error as Error;
      return data;
    },
  });

  const eventsQ = useQuery<EventRow[]>({
    queryKey: ["thread_events", id],
    enabled: Boolean(userId) && !isNew,
    staleTime: 30_000,
    queryFn: async () => {
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (cols: string) => {
            eq: (col: string, v: string) => {
              order: (col: string, opts: object) => Promise<{ data: EventRow[] | null; error: unknown }>;
            };
          };
        };
      };
      const { data, error } = await client
        .from("thread_event")
        .select("*")
        .eq("thread_id", id ?? "")
        .order("ts", { ascending: false });
      if (error) throw error as Error;
      return data ?? [];
    },
  });

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (hydrated || !threadQ.data) return;
    const t = threadQ.data;
    setForm({
      title: t.title ?? "",
      kind: t.kind ?? "casual",
      status: t.status ?? "active",
      linked_person_ids: t.linked_person_ids ?? [],
      linked_project_id: t.linked_project_id,
      next_action: t.next_action ?? "",
      next_action_due: t.next_action_due ?? "",
      notes: t.notes ?? "",
    });
    setHydrated(true);
  }, [threadQ.data, hydrated]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const save = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const payload = {
        user_id: userId,
        title: form.title.trim(),
        kind: form.kind,
        status: form.status,
        linked_person_ids: form.linked_person_ids,
        linked_project_id: form.linked_project_id,
        next_action: form.next_action.trim() || null,
        next_action_due: form.next_action_due || null,
        notes: form.notes,
      };
      const client = supabase as unknown as {
        from: (t: string) => {
          insert: (row: object) => {
            select: () => { single: () => Promise<{ data: ThreadRow | null; error: unknown }> };
          };
          update: (row: object) => {
            eq: (col: string, v: string) => {
              select: () => { single: () => Promise<{ data: ThreadRow | null; error: unknown }> };
            };
          };
        };
      };
      if (isNew) {
        const { data, error } = await client.from("thread").insert(payload).select().single();
        if (error) throw error as Error;
        return data!;
      }
      const { data, error } = await client
        .from("thread")
        .update(payload)
        .eq("id", id ?? "")
        .select()
        .single();
      if (error) throw error as Error;
      return data!;
    },
    onSuccess: (row) => {
      toast.show(isNew ? "Thread created." : "Thread updated.");
      void qc.invalidateQueries({ queryKey: ["threads_list", userId] });
      if (isNew) navigate(`/threads/${row.id}`, { replace: true });
      else void qc.invalidateQueries({ queryKey: ["thread", id] });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!id || isNew) return;
      const client = supabase as unknown as {
        from: (t: string) => {
          delete: () => { eq: (col: string, v: string) => Promise<{ error: unknown }> };
        };
      };
      const { error } = await client.from("thread").delete().eq("id", id);
      if (error) throw error as Error;
    },
    onSuccess: () => {
      toast.show("Thread deleted.");
      void qc.invalidateQueries({ queryKey: ["threads_list", userId] });
      navigate("/threads", { replace: true });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  if (!isNew && threadQ.isLoading) {
    return <SkeletonLoader variant="card" count={1} height={420} />;
  }
  if (!isNew && !threadQ.data) {
    return (
      <article>
        <BackLink />
        <p style={{ color: "var(--fg-3)" }}>Thread not found.</p>
      </article>
    );
  }

  return (
    <article aria-labelledby="thread-heading">
      <BackLink />
      <h1
        id="thread-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        {isNew ? "New thread." : form.title || "Edit thread."}
      </h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!save.isPending && form.title.trim()) save.mutate();
        }}
        style={{ display: "grid", gap: "var(--space-4)", maxWidth: 720 }}
      >
        <FormInput
          label="Title"
          value={form.title}
          onChange={(v) => setForm((s) => ({ ...s, title: v }))}
          required
          autoFocus
          placeholder="Cycling tour planning with Malena"
        />
        <Row>
          <FormSelect
            label="Kind"
            value={form.kind}
            onChange={(v) => setForm((s) => ({ ...s, kind: v }))}
            options={KINDS}
          />
          <FormSelect
            label="Status"
            value={form.status}
            onChange={(v) => setForm((s) => ({ ...s, status: v }))}
            options={STATUSES}
          />
        </Row>
        <PeoplePicker
          allPeople={peopleQ.data ?? []}
          selectedIds={form.linked_person_ids}
          onChange={(ids) => setForm((s) => ({ ...s, linked_person_ids: ids }))}
        />
        <Row>
          <FormInput
            label="Next action"
            value={form.next_action}
            onChange={(v) => setForm((s) => ({ ...s, next_action: v }))}
            placeholder="Send draft itinerary"
          />
          <FormInput
            label="Due"
            type="date"
            value={form.next_action_due}
            onChange={(v) => setForm((s) => ({ ...s, next_action_due: v }))}
          />
        </Row>
        <FormTextarea
          label="Notes"
          value={form.notes}
          onChange={(v) => setForm((s) => ({ ...s, notes: v }))}
          rows={5}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "var(--space-3)",
            marginTop: "var(--space-2)",
          }}
        >
          {isNew ? (
            <span />
          ) : (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Delete "${form.title}"? Events on this thread will also be deleted.`))
                  remove.mutate();
              }}
              disabled={remove.isPending}
              style={{
                background: "transparent",
                color: "var(--brick)",
                border: "1px solid var(--brick)",
                borderRadius: "var(--radius-card)",
                padding: "8px 14px",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: "var(--type-body-s)",
                cursor: "pointer",
              }}
            >
              <Trash2 size={14} strokeWidth={1.5} /> Delete
            </button>
          )}
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <SecondaryButton onClick={() => navigate("/threads")} disabled={save.isPending}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={save.isPending || !form.title.trim()}>
              {save.isPending ? "Saving…" : isNew ? "Create thread" : "Save changes"}
            </PrimaryButton>
          </div>
        </div>
      </form>

      {!isNew && (
        <EventsSection
          threadId={id!}
          events={eventsQ.data ?? []}
          loading={eventsQ.isLoading}
        />
      )}
    </article>
  );
}

function PeoplePicker({
  allPeople,
  selectedIds,
  onChange,
}: {
  allPeople: PersonLite[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const set = new Set(selectedIds);
  return (
    <fieldset
      style={{
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-3)",
        background: "var(--bg-3)",
      }}
    >
      <legend
        style={{
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          padding: "0 var(--space-2)",
        }}
      >
        Linked people
      </legend>
      {allPeople.length === 0 ? (
        <p style={{ margin: 0, fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
          No people yet. <Link to="/people/new" style={{ color: "var(--accent)" }}>Add one →</Link>
        </p>
      ) : (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {allPeople.map((p) => {
            const selected = set.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  if (selected) onChange(selectedIds.filter((id) => id !== p.id));
                  else onChange([...selectedIds, p.id]);
                }}
                style={{
                  padding: "4px 10px",
                  background: selected ? "var(--accent-wash)" : "transparent",
                  color: selected ? "var(--accent)" : "var(--fg-2)",
                  border: `1px solid ${selected ? "var(--accent-dim)" : "var(--hair-2)"}`,
                  borderRadius: "var(--radius-chip)",
                  fontSize: "var(--type-meta)",
                  cursor: "pointer",
                }}
              >
                {p.display_name}
              </button>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

function EventsSection({
  threadId,
  events,
  loading,
}: {
  threadId: string;
  events: EventRow[];
  loading: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { session } = useSession();
  const userId = session?.user?.id;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    kind: "message_sent",
    direction: "outbound",
    medium: "whatsapp",
    summary: "",
    notes: "",
  });

  const add = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const client = supabase as unknown as {
        from: (t: string) => {
          insert: (row: object) => Promise<{ data: unknown; error: unknown }>;
        };
      };
      const { error } = await client.from("thread_event").insert({
        user_id: userId,
        thread_id: threadId,
        ts: new Date().toISOString(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        kind: draft.kind,
        direction: draft.direction,
        medium: draft.medium,
        summary: draft.summary,
        notes: draft.notes,
      });
      if (error) throw error as Error;
    },
    onSuccess: () => {
      toast.show("Touch logged.");
      setOpen(false);
      setDraft({ ...draft, summary: "", notes: "" });
      void qc.invalidateQueries({ queryKey: ["thread_events", threadId] });
      void qc.invalidateQueries({ queryKey: ["thread", threadId] });
      void qc.invalidateQueries({ queryKey: ["threads_list", userId] });
    },
    onError: (e) => toast.show(formatError(e)),
  });

  return (
    <section style={{ marginTop: "var(--space-8)" }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-3)",
          marginBottom: "var(--space-3)",
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: "var(--type-body)",
            margin: 0,
            color: "var(--fg)",
          }}
        >
          Touch points
          <span
            style={{
              marginLeft: "var(--space-2)",
              color: "var(--fg-3)",
              fontWeight: 400,
            }}
          >
            {events.length}
          </span>
        </h2>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "6px 12px",
            background: "transparent",
            color: "var(--fg-2)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          <Plus size={14} strokeWidth={1.5} /> Log touch
        </button>
      </header>

      {open && (
        <div
          style={{
            padding: "var(--space-4)",
            background: "var(--bg-2)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            display: "grid",
            gap: "var(--space-3)",
            marginBottom: "var(--space-4)",
          }}
        >
          <Row>
            <FormSelect
              label="Kind"
              value={draft.kind}
              onChange={(v) => setDraft((s) => ({ ...s, kind: v }))}
              options={EVENT_KINDS}
            />
            <FormSelect
              label="Direction"
              value={draft.direction}
              onChange={(v) => setDraft((s) => ({ ...s, direction: v }))}
              options={[
                { value: "outbound", label: "Outbound" },
                { value: "inbound", label: "Inbound" },
                { value: "mutual", label: "Mutual" },
              ]}
            />
            <FormSelect
              label="Medium"
              value={draft.medium}
              onChange={(v) => setDraft((s) => ({ ...s, medium: v }))}
              options={EVENT_MEDIA}
            />
          </Row>
          <FormInput
            label="Summary"
            value={draft.summary}
            onChange={(v) => setDraft((s) => ({ ...s, summary: v }))}
            placeholder="Confirmed Sat morning ride"
          />
          <FormTextarea
            label="Notes"
            value={draft.notes}
            onChange={(v) => setDraft((s) => ({ ...s, notes: v }))}
            rows={3}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-3)" }}>
            <SecondaryButton onClick={() => setOpen(false)} disabled={add.isPending}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={() => add.mutate()} disabled={add.isPending}>
              {add.isPending ? "Saving…" : "Log touch"}
            </PrimaryButton>
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonLoader variant="card" count={2} height={48} />
      ) : events.length === 0 ? (
        <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          No touches logged yet. Add one to start the timeline.
        </p>
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gap: "var(--space-2)",
          }}
        >
          {events.map((e) => (
            <li key={e.id}>
              <EventRowView event={e} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EventRowView({ event }: { event: EventRow }) {
  return (
    <article
      style={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        gap: "var(--space-3)",
        padding: "var(--space-3)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <div
        style={{
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          minWidth: 96,
        }}
      >
        {formatShortDate(event.ts)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "var(--type-body-s)", color: "var(--fg)" }}>
          {event.summary || event.kind.replace(/_/g, " ")}
        </div>
        <div style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)", marginTop: 2 }}>
          {event.kind.replace(/_/g, " ")} · {event.medium} · {event.direction}
        </div>
        {event.notes && (
          <p
            style={{
              margin: "var(--space-2) 0 0",
              color: "var(--fg-2)",
              fontSize: "var(--type-meta)",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
            }}
          >
            {event.notes}
          </p>
        )}
      </div>
    </article>
  );
}

function BackLink() {
  return (
    <Link
      to="/threads"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        color: "var(--fg-3)",
        fontSize: "var(--type-meta)",
        textDecoration: "none",
      }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} /> Threads
    </Link>
  );
}
